import { t } from 'i18next'
import { Avatar } from 'primereact/avatar'
import { Button } from 'primereact/button'
import { ScrollPanel } from 'primereact/scrollpanel'
import { Skeleton } from 'primereact/skeleton'
import { TabPanel, TabView, TabViewTabChangeParams } from 'primereact/tabview'
import { Toast } from 'primereact/toast'
import { Tooltip } from 'primereact/tooltip'
import React, { useEffect, useRef, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useLocation, useNavigate } from 'react-router-dom'
import { ApprenticeData } from './ApprenticeData'
import { ExtendedPersonData } from './ExtendedPersonData'
import { GeneralPersonData } from './GeneralPersonData'
import { claims } from '../../../../claims'
import { API_PERSON } from '../../../../constants'
import {
  RequestMethod,
  SalutationCt,
  UserAccountType,
} from '../../../../model/enums/enums'
import { Apprentice, Person } from '../../../../model/person.model'
import {
  personAdded,
  personUpdated,
} from '../../../../redux/basic/person/personReducer'
import { updatePersonInStore } from '../../../../redux/basic/updatePerson'
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks'
import { AuthRoutes, PublicRoutes } from '../../../../router/routes'
import { handleRequest } from '../../../../services/api/base/handleRequest'
import {
  addPersonToDb,
  updatePersonInDb,
} from '../../../../services/api/mokaiApiService'
import { findPersonById } from '../../../../util/arraySearch'
import { getFormattedDateForMySQL } from '../../../../util/formatting'
import { getUserBigAvatarSrc } from '../../../../util/getAvatar'
import { trimSpaces } from '../../../../util/trimSpaces'
import { hasRequiredClaim } from '../../../../util/validation/hasClaim'
import { BackButton } from '../../../util/button/BackButton'
import { PhotoEditDialog } from '../../../util/dialog/PhotoEditDialog'
import { isKnownNumberFormat } from '../../../util/formatted-phone-number/FormattedPhoneNumber'
import { PersonDocumentList } from '../documents/PersonDocumentList'

/**
 * This rather unsave
 * React Hook Forms disencourages from using undefined in default values
 * We should add None types to the enum, i.e Allergy: None
 */
const defaultPerson: Person = {
  person_id: undefined,
  email: '',
  first_name: '',
  last_name: '',
  salutation: SalutationCt.NoSpecification,
  middle_name: '',
  street: '',
  street_number: '',
  onrp: 0,
  strid: 0,
  birthdate: null,
  iban: '',
  bank_name: '',
  health_insurance_number: '',
  phone: [],
  link: [],
  comment: '',
  cloth_size: 0,
  residence_permit: undefined,
  changed_by: undefined,
  confession: undefined,
  country: undefined,
  last_change: undefined,
  dominant_hand: undefined,
  is_active: 1,
  id_first_language: undefined,
  last_login: undefined,
  profile_image_ref: undefined,
  profile_image_thumbnail_ref: undefined,
  title: undefined,
  workload_in_percent: 100,
  vehicle_license: undefined,
  allergie: undefined,
  food_intolerance: undefined,
  civil_status: undefined,
  company_employee_number: undefined,
  travel_card: undefined,
  number_of_children: undefined,
}

export const EditPerson = (): JSX.Element => {
  const [isLoading, setIsLoading] = useState<boolean>(false)

  const [pId, setPid] = useState<number | undefined>()
  const [showProfilePictureEditor, setShowProfilePictureEditor] =
    useState<boolean>(false)
  const [activeIndex, setActiveIndex] = useState<number>(0)
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false)

  const toastRef = useRef<Toast>(null)

  const dispatcher = useAppDispatch()
  const navigate = useNavigate()
  const location = useLocation()

  const allPPl = useAppSelector((state) => [
    ...state.apprentices,
    ...state.employees,
    ...state.persons,
  ])

  // need to set default values

  const { loadedBasicInformation } = useAppSelector((state) => state.user)
  const userClaims = useAppSelector((state) => state.claims)

  const methods = useForm({
    defaultValues: defaultPerson,
    reValidateMode: 'onChange',
    mode: 'onSubmit',
    shouldFocusError: true,
  })

  // need to update view on profile picture change
  const selectedPerson = useAppSelector((state) =>
    [...state.apprentices, ...state.employees, ...state.persons].find(
      (pers: Apprentice | Person) => pers.person_id === pId
    )
  )

  const fetchFromDB = async (person: Person) => {
    // undefined should not happen
    if (!person.person_id) {
      console.warn('[EditPerson] No ID present, something went wrong')
      //this happens on page refresh
      setHasUnsavedChanges(false)
      return
    }
    setIsLoading(true)
    const fullPerson = await handleRequest<Person>(
      API_PERSON(person.person_id),
      RequestMethod.GET
    )
    if (!fullPerson.data) {
      console.warn('[EditPerson] Failed to get Response, something went wrong')
      setHasUnsavedChanges(false)
      setIsLoading(false)
      return
    }

    const result = { ...person, ...fullPerson.data }
    methods.reset(result)
    const storePerson = findPersonById(person.person_id, allPPl)
    if (storePerson) {
      updatePersonInStore(result, dispatcher)
    }

    setIsLoading(false)
    setHasUnsavedChanges(false)
  }

  // switch the index depending on the tab name
  // does not work with back button
  const switchTabNames = (
    tabName: string | undefined,
    person: Person
  ): void => {
    switch (tabName?.toLowerCase()) {
      case 'general':
        // do nothing
        return
      case 'dossier':
        setActiveIndex(1)
        return
      case 'apprentice':
        if (person.user_account_type === UserAccountType.Apprentice) {
          setActiveIndex(2)
        }
        // in the beginning, we are at 0 anyway
        return
    }
    // if we fall through, we set the name correctly
    window.history.replaceState(
      null,
      '',
      `${AuthRoutes.hrm}/${person.person_id}/general`
    )
  }

  // push the new name in the browser on switch of tab
  // to give a sensation of that we are actually routing
  const pushHistoryOnTabSwitch = (tabIndex: number): void => {
    // person id must exist here
    switch (tabIndex) {
      case 1:
        window.history.replaceState(
          null,
          '',
          `${AuthRoutes.hrm}/${selectedPerson?.person_id}/dossier`
        )
        return
      case 2:
        window.history.replaceState(
          null,
          '',
          `${AuthRoutes.hrm}/${selectedPerson?.person_id}/apprentice`
        )
        return
      case 0:
      default:
        window.history.replaceState(
          null,
          '',
          `${AuthRoutes.hrm}/${selectedPerson?.person_id}/general`
        )
    }
  }

  // routes to the correct tab and loads the correct person on location change
  useEffect(() => {
    if (loadedBasicInformation) {
      console.info('[EditPerson] Registered route change.')

      const substrings = location.pathname.split('/')
      const lastValue = substrings[substrings.length - 1]
      const secondLastValue = substrings[substrings.length - 2]

      // do nothing but set the stage for new user
      if (lastValue === '+' || secondLastValue === '+') {
        window.history.replaceState(null, '', `${AuthRoutes.hrm}/+`)

        setHasUnsavedChanges(false)
        return
      }
      let personID
      let tabName
      if (secondLastValue === AuthRoutes.hrm.substring(1)) {
        personID = parseInt(lastValue)
      } else {
        tabName = lastValue
        personID = parseInt(secondLastValue)
      }

      if (!personID) {
        console.warn(
          '[EditPerson] PersonId is not a valid number. Something went wrong.'
        )
        navigate(AuthRoutes.hrm)

        return
      }

      setPid(personID)

      // todo on page refresh or new page call (clicking url) it does redirect.
      // we need the full person here
      //let person = findPersonById(personID, persons.concat(employees, apprentices))
      let person = findPersonById(personID, allPPl)
      if (!person) {
        console.warn(
          '[EditPerson] Person not found either because the persons were not yet loaded or the person does not exist'
        )
        //navigate(AuthRoutes.hrm)
        setIsLoading(false)
        return
      }

      // if we want to edit a person but are not allowed, we are routed away
      // we can only land here if we have at least the claim to edit apprentices
      // therefore, if the user we want to edit is not an apprentice, we require higher rights
      if (person?.user_account_type !== UserAccountType.Apprentice) {
        if (
          !hasRequiredClaim(userClaims, claims.hrm.person.updateSinglePerson)
        ) {
          // not sure if we want to navigate to not found or hrm here
          // or if we need an error message
          console.warn(
            '[EditPerson] User does not have the correct rights to edit this person'
          )
          navigate(PublicRoutes.notFound)
        }
      }

      fetchFromDB(person)
      switchTabNames(tabName, person)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    // in case of a page refresh we do not have the all ppl loaded and need to wait until person is found
  }, [location, loadedBasicInformation])

  useEffect(() => {
    const subscription = methods.watch(async (value, { name, type }) => {
      // undefined is needed for phone adds
      if (type === 'change' || type === undefined) {
        // ignore profileimage changes since they are handled differently
        if (
          name !== 'profile_image_ref' &&
          name !== 'profile_image_thumbnail_ref'
        ) {
          setHasUnsavedChanges(true)
        }
      }
    })
    return () => subscription.unsubscribe()
  }, [])

  const checkDuplicateMail = (entry: string): boolean => {
    const person = allPPl.find((p) => p.email === entry)
    return (
      person === undefined || person.person_id === selectedPerson?.person_id
    )
  }

  const onSubmit = async (formData: Person) => {
    // here we choose to send the full form instead of just the changed fields
    // for programmatic simplicity and consistency safety

    if (formData.birthdate) {
      formData.birthdate = getFormattedDateForMySQL(formData.birthdate) as any
    }
    if (formData.iban) {
      formData.iban = trimSpaces(formData.iban)
      formData.iban = formData.iban?.replace('_', '')
    }

    // this is handled separately and on change of the profilepicture the person in the store is updated
    if (selectedPerson) {
      formData.profile_image_ref = selectedPerson.profile_image_ref
      formData.profile_image_thumbnail_ref =
        selectedPerson.profile_image_thumbnail_ref
    }

    // this is necessary so that the backend correctly generates a new ID
    formData.phone = formData.phone?.map((phone) => {
      if (isKnownNumberFormat(phone.phone_number)) {
        phone.phone_number = phone.phone_number.replace(/ /g, '')
      }
      if (phone.phone_id <= 0) {
        phone.phone_id = 0
        return phone
      } else {
        return phone
      }
    })

    // we need to create the person
    if (!formData.person_id) {
      const res = await addPersonToDb(formData)
      if (!res.data) {
        console.warn('[EditPerson] Creating an Entry was not successful')
        return
      }

      dispatcher(personAdded(res.data))
      methods.reset(res.data)
      navigate(`${AuthRoutes.hrm}/${res.data.person_id}/general`)
    } else {
      const res = await updatePersonInDb(formData.person_id, formData)

      if (!res.data) {
        console.warn('[EditPerson] Updating an Entry was not successful')
        toastRef.current?.show({
          closable: true,
          severity: 'error',
          summary: t<string>('core.action.failed.title'),
          detail: t<string>('hrm.save_personal_data_failed'),
          life: 4000,
        })
        return
      }
      const finalRes = { ...selectedPerson, ...res.data }
      dispatcher(personUpdated(formData))
      methods.reset(finalRes)
    }

    toastRef.current?.show({
      closable: true,
      severity: 'success',
      summary: t<string>('core.action.save.success.title'),
      detail: t<string>('hrm.save_personal_data'),
      life: 4000,
    })

    setHasUnsavedChanges(false)
  }

  // needed to check if we display the add new person page or the tabbed edit person page
  // not sure how save that is, we might want to create an own component for add person
  const isNotNew =
    selectedPerson?.person_id !== 0 &&
    selectedPerson?.person_id !== null &&
    selectedPerson?.person_id !== undefined
  // this is needed that in case of backbutton,
  // we don't end up with the wrong URL
  // the back button does not work when switching the tab
  // since we only route "virtually", i.e only change URL without changing anything else
  if (isNotNew) {
    pushHistoryOnTabSwitch(activeIndex)
  }

  const formContent = (): JSX.Element => {
    return (
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSubmit)}>
          <div>
            <ScrollPanel
              className={'w-full scroll-bar'}
              style={{
                height: isNotNew
                  ? 'calc(100vh - 480px)'
                  : 'calc(100vh - 240px)',
              }}
            >
              <div className={'mb-2'}>
                {
                  <GeneralPersonData
                    person={selectedPerson}
                    checkDuplicate={checkDuplicateMail}
                    userType={
                      selectedPerson?.user_account_type ||
                      UserAccountType.NoAccount
                    }
                  />
                }
              </div>
              <div className={'mb-2'}>{<ExtendedPersonData />}</div>
            </ScrollPanel>

            <div className={'mt-2 mb-2 flex gap-2 justify-content-end'}>
              <Button
                type={'submit'}
                disabled={!hasUnsavedChanges}
                label={t<string>('core.save')}
              />
            </div>
          </div>
        </form>
      </FormProvider>
    )
  }
  return (
    <>
      {isNotNew && (
        <h2 className={'pt-2'}>{`${selectedPerson.first_name} ${
          selectedPerson.middle_name || ''
        }
			${selectedPerson.last_name} (ID_${selectedPerson.person_id})`}</h2>
      )}
      <div className="card pr-2 ">
        {isLoading && (
          <div className={'image-container'}>
            <Skeleton shape={'rectangle'} className={'mb-2 button-height'} />
            <Skeleton className={'round-avatar-skeleton'} />
          </div>
        )}
        {isNotNew ? (
          <>
            <div className={'w-full inline-flex'}>
              <div className="image-container">
                <Avatar
                  image={getUserBigAvatarSrc(selectedPerson)}
                  className={'person-image ml-2 mb-2 round-avatar-skeleton'}
                  shape={'circle'}
                  onClick={() => setShowProfilePictureEditor(true)}
                />
                <div
                  className="image-overlay"
                  onClick={() => setShowProfilePictureEditor(true)}
                >
                  <i className="icon-blue person-image-icon fas fa-camera fa-3x ml-2 mb-2" />
                </div>
              </div>
              <div className={'w-full inline-flex justify-content-end'}>
                {hasUnsavedChanges && (
                  <>
                    <span
                      className={
                        'icon-size-20 pulse-element mt-1 mr-1 fa-solid fa-exclamation-circle icon-orange'
                      }
                    />
                    <Tooltip
                      target={'.fa-exclamation-circle'}
                      content={t<string>('core.action.leave_page_warning')}
                    />
                  </>
                )}
                <div className={''}>
                  <BackButton
                    translationKey={'navigation.go_back_overview'}
                    route={AuthRoutes.hrm}
                  />
                </div>
              </div>
            </div>
            <TabView
              activeIndex={activeIndex}
              onTabChange={(e: TabViewTabChangeParams) => {
                setActiveIndex(e.index)
                pushHistoryOnTabSwitch(e.index)
              }}
              renderActiveOnly={false}
            >
              <TabPanel header={t<string>('hrm.personal_data')}>
                {formContent()}
              </TabPanel>
              <TabPanel header={t<string>('dossier.ind_dossier')}>
                <div className={'mt-4 mb-2'}>
                  <PersonDocumentList
                    toastRef={toastRef}
                    person={selectedPerson}
                  />
                </div>
              </TabPanel>
              {selectedPerson.user_account_type ===
                UserAccountType.Apprentice && (
                <TabPanel header={t<string>('apprentice.apprentice_data')}>
                  <ApprenticeData person={selectedPerson} />
                </TabPanel>
              )}
            </TabView>
          </>
        ) : (
          <>
            <div className={'w-full inline-flex justify-content-end'}>
              {hasUnsavedChanges && (
                <>
                  <span
                    className={
                      'icon-size-20 pulse-element mt-1 mr-1 fa-solid fa-exclamation-circle icon-orange'
                    }
                  />
                  <Tooltip
                    target={'.fa-exclamation-circle'}
                    content={t<string>('core.action.leave_page_warning')}
                  />
                </>
              )}
              <div className={''}>
                <BackButton
                  translationKey={'navigation.go_back_overview'}
                  route={AuthRoutes.hrm}
                />
              </div>
            </div>
            {formContent()}
          </>
        )}
      </div>

      <PhotoEditDialog
        hideEditProfilePictureDialog={() => setShowProfilePictureEditor(false)}
        showProfilePictureEditor={showProfilePictureEditor}
        person={selectedPerson}
      />
      <Toast ref={toastRef} position={'bottom-right'} />
    </>
  )
}